import {
    ContentfulBlogPostFields,
    ContentfulEntry,
    ContentfulExperimentFields,
    ContentfulProjectFields,
    ContentfulVideoFields,
    formatDate,
} from '@devtinker/shared';
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getEntries } from '../lib/contentful';

type SearchResult = {
  type: 'blog' | 'project' | 'video' | 'experiment';
  title: string;
  slug: string;
  description: string;
  link: string;
  date?: string;
  tags?: string[];
  technologies?: string[];
};

const SearchResults: React.FC = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalResults, setTotalResults] = useState(0);

  useEffect(() => {
    const performSearch = async () => {
      if (!query.trim()) {
        setResults([]);
        setTotalResults(0);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const searchQuery = query.toLowerCase();
        const allResults: SearchResult[] = [];

        // Search blog posts
        const blogPosts = (await getEntries('blogPost')) as ContentfulEntry<ContentfulBlogPostFields>[];
        blogPosts.forEach((post) => {
          const searchableText = [
            post.fields.title,
            post.fields.excerpt,
            post.fields.description,
            ...(post.fields.tags || []),
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();

          if (searchableText.includes(searchQuery)) {
            allResults.push({
              type: 'blog',
              title: post.fields.title,
              slug: post.fields.slug,
              description: post.fields.excerpt || post.fields.description || '',
              link: `/blog/${post.fields.slug}`,
              date: post.fields.publishDate,
              tags: post.fields.tags,
            });
          }
        });

        // Search projects
        const projects = (await getEntries('project')) as ContentfulEntry<ContentfulProjectFields>[];
        projects.forEach((project) => {
          const searchableText = [
            project.fields.title,
            project.fields.description,
            ...(project.fields.technologies || []),
          ]
            .join(' ')
            .toLowerCase();

          if (searchableText.includes(searchQuery)) {
            allResults.push({
              type: 'project',
              title: project.fields.title,
              slug: project.fields.slug,
              description: project.fields.description,
              link: `/projects/${project.fields.slug}`,
              technologies: project.fields.technologies,
            });
          }
        });

        // Search videos
        const videos = (await getEntries('video')) as ContentfulEntry<ContentfulVideoFields>[];
        videos.forEach((video) => {
          const searchableText = [
            video.fields.title,
            video.fields.description,
            video.fields.category,
            ...(video.fields.tags || []),
          ]
            .join(' ')
            .toLowerCase();

          if (searchableText.includes(searchQuery)) {
            allResults.push({
              type: 'video',
              title: video.fields.title,
              slug: video.fields.slug,
              description: video.fields.description || '',
              link: `/videos?v=${video.fields.slug}`,
              date: video.fields.publishDate,
              tags: video.fields.tags,
            });
          }
        });

        // Search experiments
        const experiments = (await getEntries('experiment')) as ContentfulEntry<ContentfulExperimentFields>[];
        experiments.forEach((exp) => {
          const searchableText = [
            exp.fields.title,
            exp.fields.description,
            ...(exp.fields.technologies || []),
          ]
            .join(' ')
            .toLowerCase();

          if (searchableText.includes(searchQuery)) {
            allResults.push({
              type: 'experiment',
              title: exp.fields.title,
              slug: exp.fields.slug,
              description: exp.fields.description,
              link: `/experiments/${exp.fields.slug}`,
              technologies: exp.fields.technologies,
            });
          }
        });

        setResults(allResults);
        setTotalResults(allResults.length);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setLoading(false);
      }
    };

    performSearch();
  }, [query]);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'blog':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        );
      case 'project':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
            />
          </svg>
        );
      case 'video':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        );
      case 'experiment':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
            />
          </svg>
        );
      default:
        return null;
    }
  };

  const getTypeBadge = (type: string) => {
    const colors = {
      blog: 'bg-blue-100 text-blue-700',
      project: 'bg-purple-100 text-purple-700',
      video: 'bg-red-100 text-red-700',
      experiment: 'bg-green-100 text-green-700',
    };

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${colors[type as keyof typeof colors]}`}>
        {getTypeIcon(type)}
        <span className="capitalize">{type}</span>
      </span>
    );
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Searching...</h1>
          <p className="text-gray-600">Looking for "{query}"</p>
        </div>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Searching content...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!query.trim()) {
    return (
      <div className="max-w-4xl mx-auto text-center py-16">
        <div className="mb-8">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Search DevTinker</h1>
          <p className="text-gray-600">Enter a search query to find blog posts, projects, videos, and experiments.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Search Results</h1>
        <p className="text-gray-600">
          Found {totalResults} result{totalResults !== 1 ? 's' : ''} for "{query}"
        </p>
      </div>

      {results.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-12 text-center border border-gray-200">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h3 className="text-xl font-bold text-gray-900 mb-2">No results found</h3>
          <p className="text-gray-600 mb-6">Try adjusting your search query or browse by category.</p>
          <div className="flex gap-4 justify-center">
            <Link to="/blog" className="text-green-600 hover:text-green-700 font-medium">
              Browse Articles
            </Link>
            <Link to="/projects" className="text-green-600 hover:text-green-700 font-medium">
              Browse Projects
            </Link>
            <Link to="/videos" className="text-green-600 hover:text-green-700 font-medium">
              Browse Videos
            </Link>
            <Link to="/experiments" className="text-green-600 hover:text-green-700 font-medium">
              Browse Experiments
            </Link>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {results.map((result, index) => (
            <Link
              key={`${result.type}-${result.slug}-${index}`}
              to={result.link}
              className="block bg-white rounded-lg p-6 border border-gray-200 hover:border-green-500 hover:shadow-md transition-all"
            >
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {getTypeBadge(result.type)}
                    {result.date && (
                      <span className="text-sm text-gray-500">{formatDate(result.date)}</span>
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 hover:text-green-600 transition-colors">
                    {result.title}
                  </h3>
                  <p className="text-gray-600 mb-3">{result.description}</p>
                  {(result.tags || result.technologies) && (
                    <div className="flex flex-wrap gap-2">
                      {(result.tags || result.technologies)?.map((tag) => (
                        <span
                          key={tag}
                          className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="text-gray-400">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchResults;
